import * as React from 'react'

import { inject, observer } from 'mobx-react'

import { PendingRecordPanel } from '~/client/src/desktop/components/Resource/compound/PendingRecordPanel'
import { useResource } from '~/client/src/desktop/components/Resource/context'
import { useResourceDeleteAction } from '~/client/src/desktop/components/Resource/useResourceDeleteAction'
import {
  LinkedResourceEdit,
  useLinkedResourceEditor,
} from '~/client/src/shared/components/ResourceLink'
import { ResourceEdit } from '~/client/src/shared/components/ResourceViewEdit'
import { ResourceEditDetailsBody } from '~/client/src/shared/components/ResourceViewEdit/ResourceEditDetailsBody'
import { buildResourceTagSections } from '~/client/src/shared/components/ResourceViewEdit/resourceEditSectionBuilders'
import { SidePanel } from '~/client/src/shared/components/SidePanel/SidePanel'
import { useSensorEquipmentLinkField } from '~/client/src/shared/hooks/useEquipmentSensorLinkField'
import InitialState from '~/client/src/shared/stores/InitialState'

interface IProps {
  state?: InitialState
}

const StruxTagsEditPanel: React.FC<IProps> = ({ state }) => {
  const {
    state: { selectedRecordId, records },
    actions: { closeEditPanel, save },
    meta: { resourceTypeId, runtimeContext, schema },
  } = useResource()

  const projectId = state?.activeProject?.id

  const selectedRecord = React.useMemo(() => {
    if (!selectedRecordId || selectedRecordId === 'new') return null
    return records.find(r => r.id === selectedRecordId) || null
  }, [selectedRecordId, records])

  const editableRecord = React.useMemo(() => {
    if (!selectedRecord) return null
    return {
      ...selectedRecord,
      code: selectedRecord.code ?? null,
      stxResourceId: selectedRecord.stxResourceId ?? null,
      isDeleted: selectedRecord.isDeleted ?? false,
      templateId: selectedRecord.templateId ?? null,
    }
  }, [selectedRecord])

  const linkField = useSensorEquipmentLinkField({
    projectId,
    sourceRecordId: selectedRecordId === 'new' ? null : selectedRecordId,
    sourceRecord: selectedRecord,
  })

  const { openedLinkedRecordId, openLinkedRecord, closeLinkedRecord } =
    useLinkedResourceEditor(linkField.linkRootProps.linkedItem)

  const qrCodeUrl = React.useMemo(() => {
    if (!selectedRecordId || selectedRecordId === 'new') return null
    return `${window.location.origin}/resource/${resourceTypeId}/${selectedRecordId}`
  }, [selectedRecordId, resourceTypeId])

  const { onDelete, confirmDialog } = useResourceDeleteAction(selectedRecordId)

  if (!selectedRecordId) return null

  const isCreationMode = selectedRecordId === 'new'
  if (!isCreationMode && !selectedRecord) return <PendingRecordPanel />

  return (
    <>
      <SidePanel>
        <ResourceEdit.Root
          recordId={selectedRecordId}
          record={editableRecord}
          resourceTypeId={resourceTypeId}
          runtimeContext={runtimeContext}
          onClose={closeEditPanel}
          onSave={save}
          keepOpenOnCreate={true}
          onDelete={onDelete}
          qrCodeUrl={qrCodeUrl}
          resourceSchema={schema}
        >
          <ResourceEditDetailsBody
            isCreationMode={isCreationMode}
            linkField={linkField}
            onOpenLinked={openLinkedRecord}
            sections={buildResourceTagSections({
              projectId,
              infuseId: editableRecord?.infuseId,
            })}
          />
        </ResourceEdit.Root>
      </SidePanel>
      <LinkedResourceEdit
        open={!!openedLinkedRecordId}
        recordId={openedLinkedRecordId}
        projectId={projectId}
        resourceTypeId={linkField.targetResourceTypeId}
        onClose={closeLinkedRecord}
        shell={children => <SidePanel zIndex={110}>{children}</SidePanel>}
      />
      {confirmDialog}
    </>
  )
}

export default inject('state')(observer(StruxTagsEditPanel))
